import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Loader2,
  Plus,
  FileText,
  Video,
  Camera,
  Music,
  Eye,
  RefreshCw,
  XCircle as XCircleIcon,
  Wifi,
  WifiOff,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { contentApiClient } from '@/services/api/content-api';
import { queryKeys } from '@/services/query/query-client';
import { VideoDetailModal } from '@/components/VideoDetailModal';
import { BatchLaneTab, VertexBatchBadge } from '@/components/task-queue/BatchLaneTab';
import { ParkedTab } from '@/components/task-queue/ParkedTab';
import { useTaskQueue, type TaskQueueData } from '@/contexts/TaskQueueContext';
import { cancelledLabel } from '@/utils/queue-row-label';

// ============================================================================
// UUID Validation Utility
// ============================================================================

/**
 * Check if a string is a valid UUID format
 * Supports both standard UUID format (with hyphens) and compact format (without hyphens)
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const compactUuidRegex = /^[0-9a-f]{32}$/i;
  return uuidRegex.test(str) || compactUuidRegex.test(str);
}

// Phase 6: TaskQueueData interface is now imported from useTaskQueueSSE hook

interface TaskQueueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TaskQueueModal({ open, onOpenChange }: TaskQueueModalProps) {
  const [priorityContentId, setPriorityContentId] = useState('');
  const [priorityProcessing, setPriorityProcessing] = useState(false);

  // Phase 3.5: Video detail modal state
  const [videoDetailOpen, setVideoDetailOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<TaskQueueData['active_tasks'][0] | null>(null);

  // Phase 4: Reprocess state
  const [reprocessing, setReprocessing] = useState<Record<string, boolean>>({});

  // Phase 5: Cancel stuck video state
  const [canceling, setCanceling] = useState<Record<string, boolean>>({});

  // Circuit breaker state
  const [circuitBreakerStatus, setCircuitBreakerStatus] = useState<{
    is_open: boolean;
    paused_until: string | null;
    total_429s_today: number;
    consecutive_429s: number;
    threshold: number;
  } | null>(null);
  const [resettingCircuitBreaker, setResettingCircuitBreaker] = useState(false);

  // Phase 6+: Use shared SSE context for real-time updates
  // Single connection shared across dashboard and modal
  const {
    data: taskQueueData,
    isUsingSSE,
    connectionState,
    error: sseError,
    reconnect,
    refresh,
  } = useTaskQueue();

  const loading = !taskQueueData && !sseError;
  const parkedContent = taskQueueData?.parked_content ?? [];
  const parkedCount = taskQueueData?.metrics.parked_count ?? parkedContent.length;

  // CUR-1094: Vertex Batch lane snapshot. Not part of the SSE payload —
  // polled on the modal's live-refresh cadence while it is open.
  const {
    data: batchLane,
    isLoading: batchLaneLoading,
    isError: batchLaneError,
  } = useQuery({
    queryKey: queryKeys.batchLane(),
    queryFn: () => apiClient.getBatchLaneStatus(),
    enabled: open,
    refetchInterval: 15000,
  });

  // Fetch circuit breaker status when modal opens
  useEffect(() => {
    if (open) {
      apiClient.getCircuitBreakerStatus()
        .then(setCircuitBreakerStatus)
        .catch((error) => console.error('Failed to fetch circuit breaker status:', error));
    }
  }, [open]);

  // Handle circuit breaker reset
  const handleResetCircuitBreaker = async () => {
    setResettingCircuitBreaker(true);
    try {
      await apiClient.resetCircuitBreaker();
      const status = await apiClient.getCircuitBreakerStatus();
      setCircuitBreakerStatus(status);
      toast.success('Circuit breaker reset successfully. Processing can resume.');
    } catch (error) {
      console.error('Failed to reset circuit breaker:', error);
      toast.error('Failed to reset circuit breaker');
    } finally {
      setResettingCircuitBreaker(false);
    }
  };

  const handlePriorityProcess = async () => {
    if (!priorityContentId.trim()) {
      toast.error('Please enter a content ID');
      return;
    }

    setPriorityProcessing(true);
    try {
      let contentIdToQueue = priorityContentId.trim();

      // If input is not a UUID, try to resolve it as a slug
      if (!isValidUUID(contentIdToQueue)) {
        toast.info(`Resolving slug "${contentIdToQueue}" to UUID...`);

        try {
          const content = await contentApiClient.getContentDetail(contentIdToQueue);
          contentIdToQueue = content.content_id;
          toast.success(`✓ Resolved to UUID: ${contentIdToQueue.substring(0, 8)}...`);
        } catch (error) {
          console.error('Error resolving slug to UUID:', error);
          toast.error(`Failed to resolve "${contentIdToQueue}". Please check the slug or use UUID format.`);
          return;
        }
      }

      // Queue the content with the resolved UUID
      await apiClient.priorityProcessContent({
        content_id: contentIdToQueue,
        force_reprocess: true,
      });

      toast.success(`Content queued with high priority`);
      setPriorityContentId('');
      refresh();
    } catch (error) {
      console.error('Error queuing priority processing:', error);
      toast.error('Failed to queue priority processing');
    } finally {
      setPriorityProcessing(false);
    }
  };

  const handleReprocess = async (slug: string, force: boolean = false) => {
    setReprocessing(prev => ({ ...prev, [slug]: true }));
    try {
      await apiClient.reprocessContent(slug, force, 'Manual reprocess from admin dashboard');
      toast.success(`Content ${slug} queued for reprocessing`);
      refresh();
    } catch (error) {
      console.error('Error reprocessing content:', error);
      const detail = error instanceof Error ? error.message : 'Unknown error';
      const status = error && typeof error === 'object' && 'status' in error ? (error as { status: number }).status : undefined;

      // If max retries or permanent failure, offer force reprocess
      if (status === 400 && !force && (detail.includes('maximum reprocess limit') || detail.includes('permanent failure'))) {
        toast.error(detail, { duration: 6000 });
        if (window.confirm(`${detail}\n\nForce reprocess anyway?`)) {
          handleReprocess(slug, true);
          return;
        }
      } else if (status === 409) {
        // Already processing, or the source is paused/archived — the server says which.
        toast.error(detail || 'Video is currently processing. Wait for it to complete first.', { duration: 8000 });
      } else {
        toast.error(`Failed to reprocess: ${detail}`);
      }
    } finally {
      setReprocessing(prev => ({ ...prev, [slug]: false }));
    }
  };

  const handleCancelStuckVideo = async (videoId: number, slug: string) => {
    setCanceling(prev => ({ ...prev, [slug]: true }));
    try {
      const result = await apiClient.cancelStuckVideo(videoId);
      if (result.success) {
        const displayId = result.video_id ?? videoId;
        toast.success(result.message ?? `Video ${displayId} canceled and marked as failed`);
        refresh();
      } else {
        toast.warning(result.message || `Video ${videoId} is not cancelable`);
      }
    } catch (error) {
      console.error('Error canceling stuck video:', error);
      toast.error('Failed to cancel stuck video');
    } finally {
      setCanceling(prev => ({ ...prev, [slug]: false }));
    }
  };

  const handleCancelContent = async (contentId: string, slug: string) => {
    setCanceling(prev => ({ ...prev, [slug]: true }));
    try {
      const result = await apiClient.cancelContent(contentId);
      if (result.success) {
        toast.success(result.message ?? 'Content canceled and marked as failed');
        refresh();
      } else {
        toast.warning(result.message || 'Content is not cancelable');
      }
    } catch (error) {
      console.error('Error canceling content:', error);
      toast.error('Failed to cancel content');
    } finally {
      setCanceling(prev => ({ ...prev, [slug]: false }));
    }
  };

  // Phase 6: SSE hook handles data fetching automatically when `enabled: open`
  // No need for manual polling interval setup

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-foreground" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'canceled':
        return <XCircle className="h-4 w-4 text-muted-foreground" />;
      case 'in_progress':
        return <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  // Helper function to render content type badge (supports all platforms)
  const getContentTypeBadge = (contentType: string | undefined | null) => {
    if (!contentType) return null;
    if (contentType === 'article') {
      return (
        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200">
          <FileText className="h-3 w-3 mr-1 inline" />
          Article
        </Badge>
      );
    }

    if (contentType.startsWith('instagram_')) {
      const subtype = contentType.replace('instagram_', '');
      return (
        <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
          <Camera className="h-3 w-3 mr-1 inline" />
          IG {subtype}
        </Badge>
      );
    }

    if (contentType.startsWith('tiktok_')) {
      const subtype = contentType.replace('tiktok_', '');
      return (
        <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700 border-cyan-200">
          <Music className="h-3 w-3 mr-1 inline" />
          TikTok {subtype}
        </Badge>
      );
    }

    return (
      <Badge variant="default" className="text-xs">
        <Video className="h-3 w-3 mr-1 inline" />
        Video
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Task Queue Management
            {/* Connection status + Circuit breaker warning */}
            {circuitBreakerStatus?.is_open ? (
              <Badge variant="destructive" className="ml-2 text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Paused (Rate Limit)
              </Badge>
            ) : isUsingSSE ? (
              <Badge variant="default" className="ml-2 text-xs">
                <Wifi className="h-3 w-3 mr-1" />
                Live
              </Badge>
            ) : connectionState === 'polling' ? (
              <Badge variant="outline" className="ml-2 text-xs bg-gray-100 text-gray-700 border-gray-200">
                <RefreshCw className="h-3 w-3 mr-1" />
                Polling
              </Badge>
            ) : connectionState === 'reconnecting' ? (
              <Badge variant="outline" className="ml-2 text-xs bg-gray-200 text-gray-600 border-gray-300">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Reconnecting
              </Badge>
            ) : (
              <Badge variant="outline" className="ml-2 text-xs bg-gray-50 text-gray-700 border-gray-200">
                <WifiOff className="h-3 w-3 mr-1" />
                Disconnected
              </Badge>
            )}
            {/* Circuit Breaker Reset Button */}
            {circuitBreakerStatus?.is_open && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto text-xs"
                onClick={handleResetCircuitBreaker}
                disabled={resettingCircuitBreaker}
              >
                {resettingCircuitBreaker ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                Reset Circuit Breaker
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            Monitor active tasks, view queued items, and manage priority processing for video content.
            {!isUsingSSE && connectionState === 'polling' && (
              <Button
                variant="link"
                size="sm"
                className="ml-2 p-0 h-auto text-xs"
                onClick={reconnect}
              >
                Try reconnecting
              </Button>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Circuit Breaker Warning Banner */}
        {circuitBreakerStatus?.is_open && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Video Processing Paused — Rate Limited
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Circuit breaker triggered after {circuitBreakerStatus.threshold} consecutive 429 errors from Vertex AI.
                Per-minute rate limit exceeded. Processing paused until{' '}
                {circuitBreakerStatus.paused_until
                  ? new Date(circuitBreakerStatus.paused_until).toLocaleTimeString()
                  : 'unknown'}
                . Total 429 errors today: {circuitBreakerStatus.total_429s_today}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Processing will auto-resume after the pause window, or reset manually if rate limits have cleared.
              </p>
            </div>
          </div>
        )}

        {loading && !taskQueueData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Metrics Overview */}
            <div className="grid grid-cols-6 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-foreground">
                    {taskQueueData?.metrics.active_count || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Active Tasks</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-gray-500">
                    {taskQueueData?.metrics.queued_count ?? taskQueueData?.queued_content?.length ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Queued Tasks</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-foreground">{parkedCount}</div>
                  <div className="text-sm text-muted-foreground">Parked</div>
                  <div className="text-xs text-muted-foreground">no worker slot held</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-foreground">
                    {taskQueueData?.metrics.recently_processed_count || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Recently Processed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-foreground">
                    {batchLane?.in_lane_total ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Batch Lane</div>
                  <div className="text-xs text-muted-foreground">
                    today {batchLane?.today.success_count ?? 0}/{batchLane?.today.records ?? 0} ok
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm font-medium">Next Scheduled</div>
                  <div className="text-xs text-muted-foreground">
                    {taskQueueData?.metrics.next_scheduled_task?.content_id || 'None'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Priority Processing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Priority Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter slug (e.g., versace-goes-80s-video-307) or UUID"
                    value={priorityContentId}
                    onChange={(e) => setPriorityContentId(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handlePriorityProcess}
                    disabled={priorityProcessing || !priorityContentId.trim()}
                  >
                    {priorityProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Queue Priority'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Insert specific content at the front of the processing queue. Accepts slug format (e.g., versace-goes-80s-video-307) or UUID.
                </p>
              </CardContent>
            </Card>

            {/* Task Details Tabs */}
            <Tabs defaultValue="active" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="active">
                  Active Tasks ({taskQueueData?.active_tasks.length || 0})
                </TabsTrigger>
                <TabsTrigger value="queued">
                  Queued Tasks ({taskQueueData?.queued_content?.length !== taskQueueData?.metrics.queued_count
                    ? `${taskQueueData?.queued_content?.length ?? 0} of ${taskQueueData?.metrics.queued_count ?? 0}`
                    : taskQueueData?.metrics.queued_count ?? 0})
                </TabsTrigger>
                <TabsTrigger value="parked">
                  Parked ({parkedContent.length < parkedCount
                    ? `${parkedContent.length} of ${parkedCount}`
                    : parkedCount})
                </TabsTrigger>
                <TabsTrigger value="recent">
                  Recent ({taskQueueData?.recently_processed.length || 0})
                </TabsTrigger>
                <TabsTrigger value="batch">
                  Batch ({batchLane?.in_lane_total ?? 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="space-y-4">
                {taskQueueData?.active_tasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No active tasks
                  </div>
                ) : (
                  taskQueueData?.active_tasks.map((task) => (
                    <Card key={task.content_id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2 flex-1">
                            {/* Phase 3.5: Content type badge and status */}
                            <div className="flex items-center gap-2">
                              {getContentTypeBadge(task.content_type)}
                              <Badge variant="secondary" className="text-xs">
                                {task.processing_status}
                              </Badge>
                              {task.vertex_batch && <VertexBatchBadge lane={task.vertex_batch} />}
                            </div>

                            {/* Phase 3.5: Display title and slug */}
                            <div className="font-medium">
                              {task.title}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {task.slug}
                            </div>
                            {task.content_id && (
                              <div className="text-xs text-muted-foreground font-mono">
                                ID: {task.content_id.substring(0, 8)}...
                              </div>
                            )}

                            {/* CUR-1094: batch-shielded rows show the lane note instead of
                                a frozen stage progress bar — the transcript is off-box in a
                                Vertex Batch round-trip, not stuck. */}
                            {task.vertex_batch ? (
                              <div className="text-sm text-muted-foreground">
                                Transcript via Vertex Batch — progress resumes when the batch
                                round-trip completes (see Batch tab)
                              </div>
                            ) : (
                              <>
                                {/* Stage info for videos */}
                                {task.current_stage && (
                                  <div className="text-sm text-muted-foreground">
                                    Stage: {task.current_stage}
                                    {task.stages_completed !== undefined && (
                                      <> • {task.stages_completed}/6 stages completed</>
                                    )}
                                  </div>
                                )}

                                {/* Progress bar */}
                                {task.progress_percentage !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-black h-2 rounded-full transition-all"
                                        style={{ width: `${task.progress_percentage}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                                      {task.progress_percentage.toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />

                            {/* Action buttons for all active tasks */}
                            <div className="flex flex-col gap-2">
                              {/* View Details button - only for YouTube videos */}
                              {task.content_type === 'video' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedVideo(task);
                                    setVideoDetailOpen(true);
                                  }}
                                  className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                  title="View details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}

                              {/* Cancel button - works for all content types.
                                  Hidden for batch-shielded rows (CUR-1094): canceling
                                  one wastes the in-flight Vertex spend. */}
                              {!task.vertex_batch && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  // YouTube videos: use legacy video_id-based cancel
                                  if (task.content_type === 'video') {
                                    const directVideoId = typeof task.video_id === 'number' && Number.isInteger(task.video_id)
                                      ? task.video_id
                                      : undefined;

                                    if (directVideoId !== undefined) {
                                      handleCancelStuckVideo(directVideoId, task.slug);
                                      return;
                                    }

                                    const fallbackMatch = task.slug.match(/-video-(\d+)$/);
                                    if (fallbackMatch) {
                                      handleCancelStuckVideo(Number.parseInt(fallbackMatch[1], 10), task.slug);
                                      return;
                                    }
                                  }

                                  // All content types: use content_id-based cancel
                                  if (task.content_id) {
                                    handleCancelContent(task.content_id, task.slug);
                                  } else {
                                    toast.error('Content identifier unavailable for this task');
                                  }
                                }}
                                disabled={canceling[task.slug]}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                title="Cancel task"
                              >
                                {canceling[task.slug] ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4" />
                                )}
                              </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="queued" className="space-y-4">
                {!taskQueueData?.queued_content?.length ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No queued tasks
                  </div>
                ) : (
                  taskQueueData.queued_content.map((content) => (
                    <Card key={content.content_id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2 flex-1">
                            {/* Content type badge and queue state */}
                            <div className="flex items-center gap-2">
                              {getContentTypeBadge(content.content_type)}
                              <Badge variant="outline" className="text-xs">
                                {content.queue_state}
                              </Badge>
                              {content.queue_position !== null && (
                                <Badge variant="secondary" className="text-xs">
                                  #{content.queue_position}
                                </Badge>
                              )}
                              {content.vertex_batch && <VertexBatchBadge lane={content.vertex_batch} />}
                            </div>

                            {/* Title and slug */}
                            <div className="font-medium">
                              {content.title}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {content.slug}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              ID: {content.content_id.substring(0, 8)}...
                            </div>

                            {/* Progress info if available */}
                            {content.current_stage && (
                              <div className="text-xs text-muted-foreground">
                                Stage: {content.current_stage}
                                {content.stages_completed !== undefined && (
                                  <> • {content.stages_completed}/6 stages</>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <Badge variant="outline">Queued</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="parked">
                <ParkedTab items={parkedContent} total={parkedCount} />
              </TabsContent>

              <TabsContent value="recent" className="space-y-4">
                {taskQueueData?.recently_processed.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No recently processed tasks
                  </div>
                ) : (
                  taskQueueData?.recently_processed.map((job) => (
                    <Card key={job.content_id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2 flex-1">
                            {/* Phase 3.5: Content type badge and status */}
                            <div className="flex items-center gap-2">
                              {getContentTypeBadge(job.content_type)}
                              <Badge variant={job.processing_status === 'completed' ? 'default' : 'destructive'}>
                                {job.processing_status}
                              </Badge>
                            </div>

                            {/* Phase 3.5: Display title and slug */}
                            <div className="font-medium">
                              {job.title}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {job.slug}
                            </div>
                            {job.content_id && (
                              <div className="text-xs text-muted-foreground font-mono">
                                ID: {job.content_id.substring(0, 8)}...
                              </div>
                            )}

                            {/* Timestamp */}
                            {job.updated_at && (
                              <div className="text-xs text-muted-foreground">
                                Updated: {new Date(job.updated_at).toLocaleString()}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(job.processing_status)}
                            {(() => {
                              const cancelled = cancelledLabel(job);
                              return cancelled ? (
                                <Badge variant="outline" className="text-xs" title={cancelled.title}>
                                  {cancelled.label}
                                </Badge>
                              ) : null;
                            })()}

                            {/* Phase 4: Reprocess button for all items */}
                            <Button
                              variant={job.processing_status === 'completed' ? 'outline' : 'default'}
                              size="sm"
                              onClick={() => handleReprocess(job.slug, job.processing_status === 'completed')}
                              disabled={reprocessing[job.slug]}
                              className="text-xs"
                            >
                              {reprocessing[job.slug] ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3 mr-1" />
                              )}
                              {job.processing_status === 'completed' ? 'Force Reprocess' : 'Reprocess'}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="batch">
                <BatchLaneTab
                  data={batchLane}
                  isLoading={batchLaneLoading}
                  isError={batchLaneError}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>

      {/* Phase 3.5: Video Detail Modal */}
      <VideoDetailModal
        open={videoDetailOpen}
        onOpenChange={setVideoDetailOpen}
        videoData={selectedVideo}
      />
    </Dialog>
  );
}
